import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Store, User, Lock, Mail, Phone, Globe, ArrowRight, ArrowLeft, CheckCircle, Zap, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND = { blue: '#0078D4', lightBlue: '#00ADEF', green: '#33CC66', dark: '#222933' };

const RegisterStore = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [form, setForm] = useState({
    store_name: '', domain: '', owner_name: '', owner_email: '', phone: '',
    username: '', password: '', password2: ''
  });

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'domain') setDomainAvailable(null);
    if (field === 'username') setUsernameAvailable(null);
  };

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30);

  const handleStoreNameChange = (val) => {
    set('store_name', val);
    if (!form.domain || form.domain === slugify(form.store_name)) {
      set('domain', slugify(val));
    }
  };

  const checkDomain = async () => {
    if (!form.domain || form.domain.length < 3) return;
    setCheckingDomain(true);
    try {
      const res = await axios.get(`${API}/public/check-domain/${form.domain}`);
      setDomainAvailable(res.data.available);
    } catch {
      setDomainAvailable(null);
    } finally {
      setCheckingDomain(false);
    }
  };

  const checkUsername = async () => {
    if (!form.username || form.username.trim().length < 3) return;
    setCheckingUsername(true);
    try {
      const res = await axios.get(`${API}/public/check-username/${form.username.trim()}`);
      setUsernameAvailable(res.data.available);
    } catch {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const validateStep1 = () => {
    if (!form.store_name.trim()) { toast.error('Ingresa el nombre de tu tienda'); return false; }
    if (!form.domain || form.domain.length < 3) { toast.error('El dominio debe tener al menos 3 caracteres'); return false; }
    if (domainAvailable === false) { toast.error('Ese dominio ya está en uso'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!form.owner_name.trim()) { toast.error('Ingresa tu nombre'); return false; }
    if (!form.username.trim() || form.username.length < 3) { toast.error('El usuario debe tener al menos 3 caracteres'); return false; }
    if (usernameAvailable === false) { toast.error('Ese nombre de usuario ya está en uso'); return false; }
    if (!form.password || form.password.length < 4) { toast.error('La contraseña debe tener al menos 4 caracteres'); return false; }
    if (form.password !== form.password2) { toast.error('Las contraseñas no coinciden'); return false; }
    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!validateStep1()) return;
      if (domainAvailable === null) {
        await checkDomain();
        return;
      }
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/register-store`, {
        store_name: form.store_name.trim(),
        domain: form.domain,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        phone: form.phone.trim(),
        username: form.username.trim(),
        password: form.password
      });
      const { admin_path } = res.data;
      toast.success('Tienda creada exitosamente');
      setTimeout(() => navigate(`/${admin_path}/login`), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la tienda');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${BRAND.dark} 0%, #1a2030 100%)`, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '20px 16px' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'white', textDecoration: 'none', opacity: 0.7, fontSize: '0.9rem' }}>
          <ArrowLeft size={18} /> Volver a Vendix
        </Link>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px 40px' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          style={{ width: '100%', maxWidth: '480px' }}>

          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
              <Zap size={28} color={BRAND.green} />
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white' }}>Vendix<sup style={{ fontSize: '0.5em', color: BRAND.lightBlue }}>TM</sup></span>
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', marginBottom: '6px' }}>Crea tu tienda gratis</h1>
            <p style={{ color: '#8a95a8', fontSize: '0.95rem' }}>En menos de 1 minuto tendrás tu POS listo</p>
          </div>

          {/* Steps indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                width: s <= step ? '40px' : '24px', height: '4px', borderRadius: '4px',
                background: s <= step ? BRAND.blue : '#3a4252', transition: 'all 0.3s'
              }} />
            ))}
          </div>

          {/* Card */}
          <div style={{ background: 'white', borderRadius: '20px', padding: 'clamp(20px, 5vw, 32px)', boxShadow: `0 20px 60px rgba(0,0,0,0.3)` }}>
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: BRAND.dark, marginBottom: '20px' }}>
                    <Store size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} color={BRAND.blue} />
                    Datos de tu tienda
                  </h2>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Nombre de la tienda *</label>
                    <input type="text" value={form.store_name} onChange={e => handleStoreNameChange(e.target.value)}
                      placeholder="Ej: Mini Market Don José"
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                      data-testid="reg-store-name" />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Dominio de tu tienda *</label>
                    <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${domainAvailable === true ? BRAND.green : domainAvailable === false ? '#ef4444' : '#e0e5ec'}`, borderRadius: '10px', overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      <span style={{ padding: '12px 8px', background: '#f8f9fb', color: '#7a8599', fontSize: '0.75rem', borderRight: '1px solid #e0e5ec', whiteSpace: 'nowrap' }}>vendix.com/</span>
                      <input type="text" value={form.domain} onChange={e => set('domain', slugify(e.target.value))}
                        onBlur={checkDomain}
                        placeholder="mi-tienda"
                        style={{ flex: 1, padding: '12px', border: 'none', outline: 'none', fontSize: '0.95rem' }}
                        data-testid="reg-domain" />
                      {domainAvailable === true && <CheckCircle size={20} color={BRAND.green} style={{ marginRight: '10px' }} />}
                    </div>
                    {checkingDomain && <p style={{ fontSize: '0.8rem', color: BRAND.blue, marginTop: '4px' }}>Verificando disponibilidad...</p>}
                    {domainAvailable === false && <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '4px' }}>Ese dominio ya está en uso, elige otro</p>}
                    {domainAvailable === true && <p style={{ fontSize: '0.8rem', color: BRAND.green, marginTop: '4px' }}>Dominio disponible</p>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Email</label>
                      <div style={{ position: 'relative' }}>
                        <Mail size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                        <input type="email" value={form.owner_email} onChange={e => set('owner_email', e.target.value)}
                          placeholder="correo@ejemplo.com"
                          style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                          data-testid="reg-email" />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Teléfono</label>
                      <div style={{ position: 'relative' }}>
                        <Phone size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                        <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                          placeholder="0999999999"
                          style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                          data-testid="reg-phone" />
                      </div>
                    </div>
                  </div>

                  <button type="button" onClick={handleNext}
                    style={{ width: '100%', padding: '14px', borderRadius: '12px', background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.lightBlue})`, color: 'white', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    data-testid="reg-next-btn">
                    Siguiente <ArrowRight size={18} />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: BRAND.dark, marginBottom: '20px' }}>
                    <User size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} color={BRAND.blue} />
                    Tu cuenta de administrador
                  </h2>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Tu nombre *</label>
                    <div style={{ position: 'relative' }}>
                      <User size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                      <input type="text" value={form.owner_name} onChange={e => set('owner_name', e.target.value)}
                        placeholder="Tu nombre completo"
                        style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                        data-testid="reg-owner-name" />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Usuario de acceso *</label>
                    <div style={{ position: 'relative' }}>
                      <User size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                      <input type="text" value={form.username} onChange={e => set('username', e.target.value)}
                        onBlur={checkUsername}
                        placeholder="usuario123"
                        style={{ width: '100%', padding: '12px 40px 12px 36px', borderRadius: '10px', border: `1.5px solid ${usernameAvailable === true ? BRAND.green : usernameAvailable === false ? '#ef4444' : '#e0e5ec'}`, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        data-testid="reg-username" />
                      {usernameAvailable === true && <CheckCircle size={20} color={BRAND.green} style={{ position: 'absolute', right: '12px', top: '14px' }} />}
                      {usernameAvailable === false && <X size={20} color="#ef4444" style={{ position: 'absolute', right: '12px', top: '14px' }} />}
                    </div>
                    {checkingUsername && <p style={{ fontSize: '0.8rem', color: BRAND.blue, marginTop: '4px' }}>Verificando disponibilidad...</p>}
                    {usernameAvailable === false && <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '4px' }}>Ese usuario no está disponible, elige otro</p>}
                    {usernameAvailable === true && <p style={{ fontSize: '0.8rem', color: BRAND.green, marginTop: '4px' }}>Usuario disponible</p>}
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Contraseña *</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                      <input type={showPass ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                        placeholder="Mínimo 4 caracteres"
                        style={{ width: '100%', padding: '12px 40px 12px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                        data-testid="reg-password" />
                      <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: '12px', top: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#aab0bc' }}>
                        {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Confirmar contraseña *</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '14px' }} />
                      <input type={showPass ? 'text' : 'password'} value={form.password2} onChange={e => set('password2', e.target.value)}
                        placeholder="Repite la contraseña"
                        style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                        data-testid="reg-password2" />
                    </div>
                  </div>

                  {/* Summary */}
                  <div style={{ background: '#f8f9fb', borderRadius: '12px', padding: '14px', marginBottom: '20px', border: '1px solid #e8edf2' }}>
                    <p style={{ fontSize: '0.8rem', color: '#7a8599', marginBottom: '4px' }}>Resumen</p>
                    <p style={{ fontSize: '0.9rem', fontWeight: 600, color: BRAND.dark }}>{form.store_name}</p>
                    <p style={{ fontSize: '0.8rem', color: BRAND.blue }}>vendix.com/tienda/{form.domain}</p>
                    <p style={{ fontSize: '0.8rem', color: '#7a8599', marginTop: '4px' }}>Plan: <strong style={{ color: BRAND.green }}>Básico (Gratis)</strong></p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" onClick={() => setStep(1)}
                      style={{ flex: '0 0 auto', padding: '14px 20px', borderRadius: '12px', background: 'white', border: '1.5px solid #e0e5ec', color: '#5a6577', fontWeight: 600, cursor: 'pointer' }}>
                      <ArrowLeft size={18} />
                    </button>
                    <button type="submit" disabled={loading}
                      style={{ flex: 1, padding: '14px', borderRadius: '12px', background: `linear-gradient(135deg, ${BRAND.green}, #28b55a)`, color: 'white', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}
                      data-testid="reg-submit-btn">
                      {loading ? 'Creando tienda...' : 'Crear mi tienda gratis'}
                    </button>
                  </div>
                </motion.div>
              )}
            </form>
          </div>

          <p style={{ textAlign: 'center', color: '#6a7588', fontSize: '0.85rem', marginTop: '20px' }}>
            ¿Ya tienes cuenta? <Link to="/" style={{ color: BRAND.lightBlue, fontWeight: 600, textDecoration: 'none' }}>Inicia sesión</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterStore;
